import { visit } from 'unist-util-visit';

/**
 * Remark Plugin: AST-Level LaTeX Sanitizer
 * 
 * Why this exists:
 * Language models frequently hallucinate disjointed formatting, such as adding a stray
 * ` $` right before a Markdown heading (`$\boxed{...}$ $## Heading`).
 * 
 * If we use raw string Regex (like `str.replace`), it is extremely fragile and 
 * can accidentally destroy perfectly valid formatting in other contexts.
 * 
 * By operating on the Markdown AST (Abstract Syntax Tree), we can mathematically target 
 * *only* `text` nodes. This plugin traverses the tree *before* `remark-math` runs,
 * scrubbing known hallucination patterns gracefully.
 */
export default function remarkLatexSanitizer() {
    return (tree) => {
        visit(tree, 'text', (node) => {
            if (!node || typeof node.value !== 'string') return;

            // 1. Scrub orphaned trailing dollars (e.g. ` $## Extreme Analogy` or just ` $`)
            // The LLM output usually leaves ` $\n` or ` $` dangling.
            // Since this is just a text node in the AST, we know it's not actually
            // part of a code block or an existing parsed node.

            // This regex deletes any isolated `$` character sitting after a space or newline.
            // We use a safe replace here because we are ALREADY safely inside a text node.

            // We only want to target unclosed ` $` or `$$ ` that are isolated.

            // Let's perform some basic text node cleanups.
            // We look for patterns like ` $\n` or ` $ ` or `$$ $` and scrub the extra dollar signs.

            let val = node.value;

            // Scrub double-wrapped blocks `$$ $ ... $ $$` -> The stray `$` becomes isolated ` $` or `$ ` in text nodes.
            // Wait, before remark-math, `$$ $\boxed{}$ $$` is entirely one text node!
            // Yes, mdast starts with large string blocks in paragraph nodes.

            // Fix double wrapped block dollars
            val = val.replace(/\$\$\s*\$([\s\S]*?)\$(\s*\$)*\s*\$\$/g, '$$$$$1$$$$');

            // Fix orphaned trailing dollars
            val = val.replace(/(\$\$[\s\S]*?\$\$|\$(?!\$)[\s\S]*?(?<!\$)\$)\s*\$(?=[^$])/g, '$1');

            // Fix $$ blocks not on their own lines
            val = val.replace(/([^\n])\$\$/g, '$1\n\n$$');
            val = val.replace(/\$\$([^\n])/g, '$$\n\n$1');

            if (val !== node.value) {
                node.value = val;
            }
        });
    };
}
